<template>
  <div 
    v-if="shouldShowLegend" 
    class="soil-analysis-legend-widget"
    :class="{ 'disabled': disabled }"
  >
    <div class="soil-analysis-legend-content">
      <div class="legend-header">
        {{ disabled ? 'Legend (Disabled)' : 'Legend' }}
      </div>
      
      <div v-if="disabled" class="disabled-message">
        Enable Soil Nutrients in Map Layers
      </div>
      
      <div v-else-if="legendItems.length === 0" class="empty-message">
        Select a soil property to view legend
      </div>
      
      <div v-else class="legend-items">
        <div 
          v-for="(item, index) in legendItems" 
          :key="index" 
          class="legend-item"
        >
          <div class="legend-color" :style="{ backgroundColor: item.color }"></div>
          <span class="legend-text">{{ item.label }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SoilAnalysisLegend',
  props: {
    legendData: {
      type: [Object, Array],
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  
  computed: {
    shouldShowLegend() {
      // Always show when disabled to provide user feedback
      if (this.disabled) return true;
      
      // Show when there's legend data OR when we want to show empty state message
      return true; // Always show when enabled to provide consistent UI feedback
    },
    
    legendItems() {
      if (!this.legendData) return [];
      
      // Convert object format to array format
      if (typeof this.legendData === 'object' && !Array.isArray(this.legendData)) {
        return Object.entries(this.legendData).map(([label, rgbString]) => {
          // Check if label is a simple number and format as "Index X"
          const formattedLabel = /^\d+$/.test(label.trim()) ? `Index ${label}` : label;
          
          return {
            label: formattedLabel,
            color: `rgb(${rgbString})`
          };
        });
      }
      
      // If already an array, return as-is
      return Array.isArray(this.legendData) ? this.legendData : [];
    }
  }
}
</script>

<style scoped>
/* Placed by FarmMap, in the map's bottom-left corner above the property selector */
.soil-analysis-legend-widget {
  pointer-events: auto;
  max-width: 220px;
}

.soil-analysis-legend-content {
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(10px);
  transition: all 0.2s ease;
  max-height: 300px;
  overflow-y: auto;
}

.soil-analysis-legend-content:hover {
  background: rgba(255, 255, 255, 1);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

/* Disabled state styling */
.soil-analysis-legend-widget.disabled .soil-analysis-legend-content {
  background: rgba(245, 245, 245, 0.9);
  color: #9ca3af;
}

.soil-analysis-legend-widget.disabled .soil-analysis-legend-content:hover {
  background: rgba(245, 245, 245, 0.9);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.soil-analysis-legend-widget.disabled .legend-text,
.soil-analysis-legend-widget.disabled .legend-header {
  color: #9ca3af;
}

.legend-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  font-weight: 600;
  color: #374151;
  font-size: 13px;
}

.legend-icon {
  width: 16px;
  height: 16px;
  color: #6366f1;
  flex-shrink: 0;
}

.legend-items {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 0;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.legend-text {
  font-size: 12px;
  color: #374151;
  line-height: 1.3;
  font-weight: 500;
}

.disabled-message {
  font-size: 11px;
  color: #6b7280;
  line-height: 1.4;
  text-align: center;
  padding: 8px;
  font-style: italic;
}

.empty-message {
  font-size: 11px;
  color: #6b7280;
  line-height: 1.4;
  text-align: center;
  padding: 8px;
  font-style: italic;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .soil-analysis-legend-widget {
    max-width: 200px;
  }
  
  .soil-analysis-legend-content {
    padding: 10px;
    max-height: 250px;
  }
  
  .legend-header {
    font-size: 12px;
  }
  
  .legend-text {
    font-size: 11px;
  }
}
</style>